/**
 * @fileoverview Audit event search query hook.
 * @description Wraps getAuditEventsPage in a React Query query keyed by its parameters, keeping the
 * previous page on screen while the next loads. Never refetches on window focus or retries: every
 * successful search is itself written to the audit trail and counts against a per-user rate limit
 * (15 a minute), so an automatic refetch would both pollute the trail and risk a 429.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { AUDIT_QUERY_KEYS } from '@/features/audit/constants/audit-query-keys.constants'
import { getAuditEventsPage } from '@/features/audit/services/audit.service'

import type { AuditEventsPage, AuditEventsParams } from '@/features/audit/types/audit.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches one page of audit events.
 * @param {AuditEventsParams | null} params - The date range, filters, sort and page, or null while
 * the filters can't be searched yet (an invalid date range), which keeps the query idle.
 * @returns {import('@tanstack/react-query').UseQueryResult<AuditEventsPage, ApiError>} The query
 * result.
 */
export function useAuditEvents(params: AuditEventsParams | null) {
  return useQuery<AuditEventsPage, ApiError>({
    queryKey: [...AUDIT_QUERY_KEYS.events, params],
    queryFn: () => getAuditEventsPage(params as AuditEventsParams),
    placeholderData: keepPreviousData,
    enabled: params !== null,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
